import React from 'react'

/**
 * SSR module preload tracking.
 *
 * Drop-in replacement for React.lazy() at route definition sites. When a
 * tracked component resolves during SSR, it adds its source path to a
 * per-request store so the server can emit <link rel="modulepreload">
 * tags for those chunks. The browser fetches them in parallel with HTML
 * parsing — by the time React hydrates the chunks are already loaded
 * and Suspense never falls through to its fallback.
 *
 * On the client this is plain React.lazy() with no tracking — the store
 * is only set by entry-server.jsx, never by entry-client.jsx.
 */

// AsyncLocalStorage is set by entry-server.jsx via setModuleTrackingStore.
// It stays null on the client (and in tests / dev tools) so trackedLazy
// becomes a no-op there.
let _moduleTrackingStore = null

export function setModuleTrackingStore(store) {
  _moduleTrackingStore = store
}

/**
 * Drop-in replacement for React.lazy() that records the source path
 * during SSR rendering.
 *
 * Usage:
 *   const Home = trackedLazy(() => import('pages/home'), 'src/pages/home/Home.jsx')
 *
 * The second argument MUST match a key in build/.vite/ssr-manifest.json.
 * To find the right value: build with `npm run build:ssr`, open the
 * generated manifest, find the entry whose value contains the chunk you
 * expect (e.g. Home-*.js), and use that exact key.
 */
export const trackedLazy = (importFn, sourcePath) => {
  const LazyComponent = React.lazy(importFn)
  // Track at COMPONENT RENDER time, not at import time. React.lazy caches
  // its importFn after first resolution — wrapping the import would only
  // fire tracking on the first request to a route after server boot, and
  // be empty on every subsequent request. The wrapper here runs every
  // render, so each request gets the full set of modules used.
  function TrackedComponent(props) {
    const ctx = _moduleTrackingStore?.getStore()
    if (ctx?.modules) ctx.modules.add(sourcePath)
    return React.createElement(LazyComponent, props)
  }
  TrackedComponent.displayName = `Tracked(${sourcePath})`
  return TrackedComponent
}
